import React from 'react';
import { observer } from 'mobx-react';
import { CircularProgress, Card, Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { connect } from 'formik';
import { getSnapshot } from 'mobx-state-tree';

import { useStore } from 'core/store';
import useErrors from 'common/hooks/use-errors';
import useSession from 'core/hooks/use-session';
import useAdditionalOffers from 'offer/hooks/use-additional-offers';
import { InternalUwNotes } from 'common/components/internal-uw-notes';
import { RecalculatingDim } from '../recalculating-dim';
import { PolicyErrors } from './components/policy-errors';
import { Additionals } from './components/additionals';
import { DiscountsCard } from './components/discounts-card';
import { AdditionalOffersSection } from './components/additional-offers-section';
import { PriceDetailsCard } from './components/price-details-card';
import { PolicyPickerCard } from './components/policy-picker-card';
import { DocumentsSharing } from './components/documents-sharing';
import { OfferTools } from './components/offer-tools';
import { THEME_VARIANTS } from 'core/components/theme/theme-variant';
import useStyles from './sidebar.styles';

// Reskin: related cards sit 8px apart inside a group and groups 24px apart, so spacing shows the grouping.
// Classic renders the cards straight into the sidebar, unchanged.
const SidebarGroup = ({ className, children }) => (className ? <div className={className}>{children}</div> : children);

const Sidebar = observer(() => {
  const classes = useStyles();
  const { themeVariant } = useTheme();
  const groupClassName = themeVariant === THEME_VARIANTS.RESKIN ? classes.group : undefined;
  const { offer: store } = useStore();
  const { isInternalAgent } = useSession();
  const getErrorsAndRejections = (code) => code >= 4003;
  const errorsAndRejections = useErrors(getSnapshot(store.errors), getErrorsAndRejections);
  const additionalOffers = useAdditionalOffers(store);

  const isLoading = (store.loading && !store.recalculatingPrice) || !store.offer || additionalOffers.loading;

  if (isLoading) {
    return (
      <>
        <div className={classes.columnHeader} />
        <Card className={classes.container}>
          <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
            <CircularProgress className={classes.loader} />
          </Grid>
        </Card>
      </>
    );
  }

  return (
    <RecalculatingDim id="offer_details" className={classes.sidebar}>
      <SidebarGroup className={groupClassName}>
        <div className={classes.columnHeader} />
        {/* Staff-only internal UW notes — self-gates on the session's isAgency flag; never shown to agents. #35208.
            Key on the sticky webUserId, not offer.id: offer.id is regenerated on every mutation
            (re-rate / approve-for-checkout), which orphaned the notes. Fall back to id. #35323 */}
        <InternalUwNotes offerId={store?.offer?.webUserId || store?.offer?.id} />
      </SidebarGroup>
      <SidebarGroup className={groupClassName}>
        <PolicyPickerCard />
        <PriceDetailsCard />
        {!store.isBranchPlusOnly && <DiscountsCard />}
      </SidebarGroup>
      {(isInternalAgent ||
        store.shouldShowAdditionalPaymentsSection ||
        additionalOffers.shouldShowAdditionalOffers) && (
        <SidebarGroup className={groupClassName}>
          {isInternalAgent && <OfferTools />}
          {store.shouldShowAdditionalPaymentsSection && <Additionals />}
          {additionalOffers.shouldShowAdditionalOffers && (
            <AdditionalOffersSection {...additionalOffers.additionalOffersProps} />
          )}
        </SidebarGroup>
      )}
      {errorsAndRejections.length > 0 && (
        <SidebarGroup className={groupClassName}>
          <PolicyErrors />
        </SidebarGroup>
      )}
      <SidebarGroup className={groupClassName}>
        <DocumentsSharing />
      </SidebarGroup>
    </RecalculatingDim>
  );
});

export default connect(Sidebar);
